import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { isCentralRole } from './authSlice';

const COLLECTION = 'medicalTransportOrders';

const toTimestampOrNull = (value) => {
  if (!value) return null;
  if (value?.toDate || value?.seconds) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(12, 0, 0, 0);
  return Timestamp.fromDate(date);
};

const normalizeOrder = (docSnapshot) => ({
  id: docSnapshot.id,
  ...docSnapshot.data()
});

const sortOrders = (orders) => [...orders].sort((a, b) => {
  const getMs = (value) => {
    if (!value) return 0;
    if (value.toDate) return value.toDate().getTime();
    if (value.seconds) return value.seconds * 1000;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  };
  return getMs(b.updatedAt || b.createdAt) - getMs(a.updatedAt || a.createdAt);
});

const buildPayload = (data, currentUser, isCreate = false) => {
  const operatorId = data.operatorId || currentUser?.operatorId || null;
  const visibleToOperatorIds = operatorId ? [operatorId] : [];

  return {
    customerId: data.customerId || null,
    patientName: (data.patientName || '').trim(),
    patientPhone: (data.patientPhone || '').trim(),
    healthInsuranceName: (data.healthInsuranceName || '').trim(),
    healthInsuranceId: data.healthInsuranceId || null,
    insuranceNumber: (data.insuranceNumber || '').trim(),
    prescriptionNumber: (data.prescriptionNumber || '').trim(),
    approvalNumber: (data.approvalNumber || '').trim(),
    approvalRequired: data.approvalRequired !== false,
    approvalStatus: data.approvalStatus || 'missing',
    approvalRequestedAt: toTimestampOrNull(data.approvalRequestedAt),
    approvalApprovedAt: toTimestampOrNull(data.approvalApprovedAt),
    approvalValidUntil: toTimestampOrNull(data.approvalValidUntil),
    validFrom: toTimestampOrNull(data.validFrom),
    validUntil: toTimestampOrNull(data.validUntil),
    maxRides: Number(data.maxRides) || 0,
    usedRides: Number(data.usedRides) || 0,
    plannedRides: Number(data.plannedRides) || 0,
    routeScope: data.routeScope || 'fixed_route',
    pickupAddress: (data.pickupAddress || '').trim(),
    pickupLatitude: data.pickupLatitude ? Number(data.pickupLatitude) : null,
    pickupLongitude: data.pickupLongitude ? Number(data.pickupLongitude) : null,
    destinationAddress: (data.destinationAddress || '').trim(),
    destinationLatitude: data.destinationLatitude ? Number(data.destinationLatitude) : null,
    destinationLongitude: data.destinationLongitude ? Number(data.destinationLongitude) : null,
    treatmentType: data.treatmentType || 'other',
    transportRequirementMode: data.transportRequirementMode || 'vehicle_type',
    requiredCapabilities: data.transportRequirementMode === 'capability' && Array.isArray(data.requiredCapabilities)
      ? data.requiredCapabilities
      : [],
    defaultVehicleType: data.transportRequirementMode === 'capability'
      ? null
      : (Number(data.defaultVehicleType) || 1),
    defaultNotes: (data.defaultNotes || '').trim(),
    escortRequired: data.escortRequired === true,
    copaymentStatus: data.copaymentStatus || 'unknown',
    copaymentAmountEur: data.copaymentAmountEur ? Number(data.copaymentAmountEur) : null,
    documentStatus: data.documentStatus || 'incomplete',
    missingDocumentReasons: Array.isArray(data.missingDocumentReasons) ? data.missingDocumentReasons : [],
    billingStatus: data.billingStatus || 'not_ready',
    operatorId,
    visibleToOperatorIds,
    archived: data.archived === true,
    updatedAt: serverTimestamp(),
    updatedBy: currentUser?.uid || null,
    ...(isCreate ? {
      createdAt: serverTimestamp(),
      createdBy: currentUser?.uid || null
    } : {})
  };
};

export const fetchMedicalTransportOrders = createAsyncThunk(
  'medicalTransportOrders/fetchMedicalTransportOrders',
  async (_, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!currentUser) throw new Error('Nicht angemeldet');

      const ordersQuery = isCentralRole(currentUser)
        ? collection(db, COLLECTION)
        : query(
            collection(db, COLLECTION),
            where('visibleToOperatorIds', 'array-contains', currentUser.operatorId || '__none__')
          );

      const snapshot = await getDocs(ordersQuery);
      return sortOrders(snapshot.docs.map(normalizeOrder).filter(order => order.archived !== true));
    } catch (error) {
      console.error('Fehler beim Laden der Verordnungen:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMedicalTransportOrderById = createAsyncThunk(
  'medicalTransportOrders/fetchMedicalTransportOrderById',
  async (id, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!currentUser) throw new Error('Nicht angemeldet');

      const orderDoc = await getDoc(doc(db, COLLECTION, id));
      if (!orderDoc.exists()) throw new Error('Verordnung nicht gefunden');

      const order = normalizeOrder(orderDoc);
      if (!isCentralRole(currentUser) && !order.visibleToOperatorIds?.includes(currentUser.operatorId)) {
        throw new Error('Keine Berechtigung fuer diese Verordnung');
      }
      return order;
    } catch (error) {
      console.error('Fehler beim Laden der Verordnung:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const createMedicalTransportOrder = createAsyncThunk(
  'medicalTransportOrders/createMedicalTransportOrder',
  async (orderData, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!currentUser) throw new Error('Nicht angemeldet');

      const payload = buildPayload(orderData, currentUser, true);
      const docRef = await addDoc(collection(db, COLLECTION), payload);
      return { id: docRef.id, ...payload };
    } catch (error) {
      console.error('Fehler beim Erstellen der Verordnung:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateMedicalTransportOrder = createAsyncThunk(
  'medicalTransportOrders/updateMedicalTransportOrder',
  async ({ id, ...orderData }, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!currentUser) throw new Error('Nicht angemeldet');

      const existingDoc = await getDoc(doc(db, COLLECTION, id));
      if (!existingDoc.exists()) throw new Error('Verordnung nicht gefunden');
      const existing = existingDoc.data();
      if (!isCentralRole(currentUser) && !existing.visibleToOperatorIds?.includes(currentUser.operatorId)) {
        throw new Error('Keine Berechtigung fuer diese Verordnung');
      }

      const payload = buildPayload(orderData, currentUser, false);
      await updateDoc(doc(db, COLLECTION, id), payload);
      return { id, ...existing, ...payload };
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Verordnung:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const archiveMedicalTransportOrder = createAsyncThunk(
  'medicalTransportOrders/archiveMedicalTransportOrder',
  async (id, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!currentUser) throw new Error('Nicht angemeldet');
      await updateDoc(doc(db, COLLECTION, id), {
        archived: true,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });
      return id;
    } catch (error) {
      console.error('Fehler beim Archivieren der Verordnung:', error);
      return rejectWithValue(error.message);
    }
  }
);

const medicalTransportOrdersSlice = createSlice({
  name: 'medicalTransportOrders',
  initialState: {
    orders: [],
    currentOrder: null,
    isLoading: false,
    isSaving: false,
    error: null
  },
  reducers: {
    clearMedicalTransportOrdersError: (state) => {
      state.error = null;
    },
    clearCurrentMedicalTransportOrder: (state) => {
      state.currentOrder = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMedicalTransportOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMedicalTransportOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders = action.payload;
      })
      .addCase(fetchMedicalTransportOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchMedicalTransportOrderById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMedicalTransportOrderById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchMedicalTransportOrderById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createMedicalTransportOrder.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createMedicalTransportOrder.fulfilled, (state, action) => {
        state.isSaving = false;
        state.orders.unshift(action.payload);
        state.currentOrder = action.payload;
      })
      .addCase(createMedicalTransportOrder.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      .addCase(updateMedicalTransportOrder.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateMedicalTransportOrder.fulfilled, (state, action) => {
        state.isSaving = false;
        state.orders = state.orders.map(order => order.id === action.payload.id ? action.payload : order);
        state.currentOrder = action.payload;
      })
      .addCase(updateMedicalTransportOrder.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      .addCase(archiveMedicalTransportOrder.fulfilled, (state, action) => {
        state.orders = state.orders.filter(order => order.id !== action.payload);
      })
      .addCase(archiveMedicalTransportOrder.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const {
  clearMedicalTransportOrdersError,
  clearCurrentMedicalTransportOrder
} = medicalTransportOrdersSlice.actions;

export default medicalTransportOrdersSlice.reducer;
